import { Heading } from '@customerio/pluma-components/react';
import { isUnknownPeopleTotal } from '@/services/db/customer/entity';
import { formatNumber, formatNumberAndPluralize } from '@/utils/number';

type TableListCountProps = {
  /** Select-all across the whole result set. */
  allSelected: boolean;
  /** Total matching records; `-1` when the backend reports the total as unknown. */
  totalCount: number;
  /** Number of loaded rows currently checked. */
  selectedCount: number;
  /** The singular people noun (taxonomy term), e.g. "person" or "profile". */
  singularName: string;
};

function formatTotal(totalCount: number, singularName: string): string {
  if (isUnknownPeopleTotal(totalCount)) return 'many';
  if (totalCount === 1) return `1 ${singularName}`;
  return formatNumberAndPluralize(totalCount, singularName);
}

function allSelectedLabel(totalCount: number, singularName: string): string {
  if (totalCount === 1) return `1 ${singularName} selected`;
  if (isUnknownPeopleTotal(totalCount)) return 'All selected';
  return `All ${formatNumberAndPluralize(totalCount, singularName)} selected`;
}

function partialSelectionLabel(totalCount: number, selectedCount: number, singularName: string): string {
  const total = formatTotal(totalCount, singularName);
  if (selectedCount <= 0) return total;
  return `${formatNumber(selectedCount)} out of ${total} selected`;
}

/**
 * The count copy beside the bulk actions: "1,234 people",
 * "2 out of 3 people selected", "All 51 people selected", "many" when the
 * total is unknown; `null` when nothing is selected and the total is unknown.
 */
export function tableListCountLabel({
  allSelected,
  totalCount,
  selectedCount,
  singularName,
}: TableListCountProps): string | null {
  if (selectedCount <= 0 && isUnknownPeopleTotal(totalCount)) return null;
  if (allSelected) return allSelectedLabel(totalCount, singularName);
  return partialSelectionLabel(totalCount, selectedCount, singularName);
}

/** The result/selection count heading beside the bulk actions. */
export function TableListCount(props: TableListCountProps) {
  const label = tableListCountLabel(props);
  if (label == null) return null;
  return (
    <Heading level="3" mr="150">
      {label}
    </Heading>
  );
}
